import { useState } from "react";
import {
  EditorContent,
  Mark,
  Node,
  mergeAttributes,
  useEditor,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";

import type { GuidanceEnvelope } from "@/hooks/use-intent-hub";

// The two chips an author drops into guidance (intents-api
// guidance/schema.py). Rendered read-only, so only the attrs the
// label needs are declared; the rest ride along untouched.
const CHIP_CLASS =
  "inline-flex items-center rounded border bg-muted/60 px-1.5 py-px align-baseline font-mono text-[11px] leading-5";

const ToolRef = Node.create({
  name: "tool_ref",
  group: "inline",
  inline: true,
  atom: true,
  addAttributes: () => ({
    ref_type: {},
    ref: {},
    label: {},
    use_id: {},
  }),
  renderHTML: ({ node, HTMLAttributes }) => [
    "span",
    mergeAttributes(HTMLAttributes, {
      class: CHIP_CLASS,
      title: `${String(node.attrs.ref_type)}: ${String(node.attrs.ref)}`,
    }),
    String(node.attrs.label),
  ],
});

const AttrRef = Node.create({
  name: "attr_ref",
  group: "inline",
  inline: true,
  atom: true,
  addAttributes: () => ({
    ref_type: {},
    ref: {},
    output: {},
    label: {},
    use_id: {},
    parent_use_id: {},
  }),
  renderHTML: ({ node, HTMLAttributes }) => [
    "span",
    mergeAttributes(HTMLAttributes, {
      class: `${CHIP_CLASS} italic`,
      title: `${String(node.attrs.ref)}.${String(node.attrs.output)}`,
    }),
    String(node.attrs.label),
  ],
});

// The backend allows an underline mark; StarterKit doesn't ship one.
const Underline = Mark.create({
  name: "underline",
  renderHTML: ({ HTMLAttributes }) => ["u", HTMLAttributes, 0],
});

const GUIDANCE_EXTENSIONS = [
  StarterKit.configure({ heading: false, codeBlock: false, blockquote: false }),
  Link.configure({ openOnClick: false }),
  Underline,
  ToolRef,
  AttrRef,
];

/** The stored guidance document as the Builder shows it, read-only. */
export function GuidanceDoc({ guidance }: { guidance: GuidanceEnvelope }) {
  const [contentError, setContentError] = useState<string | null>(null);
  const editor = useEditor(
    {
      extensions: GUIDANCE_EXTENSIONS,
      content: guidance.doc,
      editable: false,
      // Without the check TipTap swallows an unknown node/mark and
      // renders an empty document, which would read as "no guidance".
      enableContentCheck: true,
      onContentError: ({ error }) => {
        setContentError(error.message);
      },
    },
    [guidance],
  );
  if (contentError) {
    return (
      <p role="alert" className="text-destructive text-sm">
        This document uses markup the viewer doesn&apos;t know:{" "}
        <span className="font-mono text-xs">{contentError}</span>
      </p>
    );
  }
  return (
    <EditorContent
      editor={editor}
      className="text-foreground/85 text-sm leading-relaxed [&_.ProseMirror]:break-words [&_.ProseMirror]:outline-none [&_li]:ml-5 [&_ol]:list-decimal [&_p+p]:mt-2 [&_ul]:list-disc"
    />
  );
}
